import type { ICaaRefreshTokenParams, ICaaToken, ICaaTokenResponse } from "../interface/customer-account";
import { discoverCaaEndpoints } from "./caaDiscovery";

/**
 * Customer Account API OAuth 2.0 토큰 요청
 * 토스트·라우팅·store는 스토어마다 달라 여기서 다루지 않고, 앱이 결과/콜백으로 처리
 * https://shopify.dev/docs/api/customer
 */

/** token endpoint가 요청을 거부했을 때 (HTTP 4xx/5xx) — 네트워크 오류와 구분하기 위한 에러 */
export class CaaTokenRejectedError extends Error {}

/** token endpoint 호출 — authorization_code 교환과 refresh_token 갱신이 응답 형태가 같아 함께 사용 */
const requestCaaToken = async (shopDomain: string, params: Record<string, string>): Promise<ICaaToken> => {
  const { tokenEndpoint } = await discoverCaaEndpoints(shopDomain);

  const response = await fetch(tokenEndpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams(params),
  });

  if (!response.ok) throw new CaaTokenRejectedError(`[CAA] token request failed (${params.grant_type}): ${response.status}`);

  const data = (await response.json()) as ICaaTokenResponse;

  return {
    accessToken: data.access_token,
    // Shopify가 refresh 시 새 refresh_token을 내려줄 수 있어 매번 덮어씌움
    refreshToken: data.refresh_token,
    idToken: data.id_token,
    expiresAt: new Date(Date.now() + data.expires_in * 1000).toISOString(),
  };
};

/**
 * refresh_token으로 새 토큰을 발급받음
 * token endpoint가 거부하면 CaaTokenRejectedError, 네트워크 오류면 fetch 에러를 그대로 던짐
 */
export const refreshCaaToken = ({ shopDomain, clientId, refreshToken }: ICaaRefreshTokenParams): Promise<ICaaToken> =>
  requestCaaToken(shopDomain, { grant_type: "refresh_token", client_id: clientId, refresh_token: refreshToken });
